// enter-nav.js
// Enter “pure” → jump to next cell; Alt/Ctrl/Shift+Enter in textarea → go to new line.

// Get a list of focusable elements within a root
export function getFocusable(root) {
    const list = Array.from(root.querySelectorAll('input, select, textarea, button, [tabindex]:not([tabindex="-1"])')).filter((el) => {
        const cs = window.getComputedStyle(el);
        const visible = cs.visibility !== 'hidden' && cs.display !== 'none' && el.offsetParent !== null;
        const enabled = !el.disabled && !el.readOnly;
        if (!visible || !enabled || el.hasAttribute('data-enter-skip')) return false;
        // only radio checked
        if (el.tagName === 'INPUT' && (el.type || '').toLowerCase() === 'radio') {
            return !!el.checked;
        }
        return true;
    });

    list.sort((a, b) => {
        const ta = parseInt(a.getAttribute('tabindex') || '0', 10);
        const tb = parseInt(b.getAttribute('tabindex') || '0', 10);
        if (ta !== tb) return ta - tb;
        return 0;
    });
    return list;
}

/* =============================
 * Navigation core
 * ============================= */
// Focus to the next element in the list getFocusable(root) from current.
export function focusNext(root, current, { loop = true } = {}) {
    const list = getFocusable(root);
    const i = list.indexOf(current);
    const next = i >= 0 ? list[i + 1] || (loop ? list[0] : null) : list[0];

    if (!next) return;

    if (isHasInput(next)) {
        next.click?.();

        const inner = findClickableWithin(next);
        inner?.click?.();

        const list2 = getFocusable(root);
        const i2 = list2.indexOf(next);
        const next2 = i2 >= 0 ? list2[i2 + 1] || (loop ? list2[0] : null) : list2[0];
        if (next2) {
            next2.focus?.();
            if (typeof next2.select === 'function') next2.select();
        }
        return;
    }

    next.focus?.();
    if (typeof next.select === 'function') next.select();
}

/* =============================
 * Textarea helpers
 * ============================= */
// Automatically insert a newline character \n at the cursor position in the <textarea>.
function insertNewline(textarea) {
    const start = textarea.selectionStart ?? textarea.value.length;
    const end = textarea.selectionEnd ?? textarea.value.length;
    const v = textarea.value ?? '';
    textarea.value = v.slice(0, start) + '\n' + v.slice(end);
    const pos = start + 1;
    textarea.setSelectionRange?.(pos, pos);
    textarea.dispatchEvent(new Event('input', { bubbles: true }));
}

function isPlainEnter(e) {
    return e.key === 'Enter' && !e.isComposing && !e.altKey && !e.ctrlKey && !e.shiftKey && !e.metaKey;
}

function isTextareaLike(t) {
    return t.tagName === 'TEXTAREA' || t.isContentEditable || t.getAttribute?.('role') === 'textbox';
}

function isButtonLike(el) {
    if (!el) return false;
    const tag = el.tagName;
    if (tag === 'BUTTON') return true;
    if (tag === 'INPUT') {
        const t = (el.type || '').toLowerCase();
        return t === 'button' || t === 'submit' || t === 'reset';
    }
    // btn role=button
    return el.getAttribute?.('role') === 'button';
}

function isHasInput(el) {
    return !!el && el.classList?.contains('has-input');
}

function findClickableWithin(scope) {
    if (!scope || !scope.querySelector) return null;
    return scope.querySelector('input, button, [role="button"], [role="radio"], a, [tabindex]') || null;
}

// Main handler for the keydown event in the capture phase.
function onKeydown(e, root, opts) {
    if (e.key !== 'Enter' || e.isComposing) return;
    const t = e.target;
    if (!t) return;

    // Skip if element has data-enter-skip attribute
    if (t.hasAttribute && t.hasAttribute('data-enter-skip')) {
        return;
    }

    // textarea + (Alt/Ctrl/Shift)+Enter => go to new line
    if (isTextareaLike(t) && (e.altKey || e.ctrlKey || e.shiftKey)) {
        e.preventDefault();
        e.stopImmediatePropagation?.();
        insertNewline(t);
        return;
    }

    // Enter only => to next cell
    if (isPlainEnter(e)) {
        if (isButtonLike(t)) {
            e.preventDefault();
            e.stopImmediatePropagation?.();
            t.click?.();
            return;
        }
        e.preventDefault();
        focusNext(root, t, opts);
    }
}

/* =============================
 * Public attach APIs
 * ============================= */
// limit the scope to a specific container
export function attach(container, options = {}) {
    const handler = (e) => onKeydown(e, container, options);
    container.addEventListener('keydown', handler, true);
    return () => container.removeEventListener('keydown', handler, true);
}

// Global attach to the whole document
export function attachGlobal({ loop = true, scopeSelector = '[data-enter-nav]' } = {}) {
    const handler = (e) => {
        if (e.key !== 'Enter' || e.isComposing) return;
        const t = e.target;
        if (!t) return;

        // Skip if element has data-enter-skip attribute
        if (t.hasAttribute && t.hasAttribute('data-enter-skip')) {
            return;
        }

        if (isTextareaLike(t) && (e.altKey || e.ctrlKey || e.shiftKey)) {
            e.preventDefault();
            e.stopImmediatePropagation?.();
            insertNewline(t);
            return;
        }
        if (isPlainEnter(e)) {
            const scope = t.closest(scopeSelector) || document.body;
            if (isButtonLike(t)) {
                e.preventDefault();
                e.stopImmediatePropagation?.();
                t.click?.();
                return;
            }
            e.preventDefault();
            focusNext(scope, t, { loop });
        }
    };
    document.addEventListener('keydown', handler, true);
    return () => document.removeEventListener('keydown', handler, true);
}
